<template>
  <div class="role h-100">
    <template v-if="showPermissions">
      <permissions @close="showPermissions = false" />
    </template>
    <template v-else>
      <form-wrapper full-width>
        <template v-slot:body>
          <div class="role__form">
            <div class="col-12 col-md-8 m-auto">
              <custom-alert
                icon-name="InfoAlt"
                items-center
                variant="light"
              >
                A role contains a set of permissions that allows you to perform
                specific actions on Captain resources. To make permissions
                available to users, you grant roles to the members.
              </custom-alert>
            </div>

            <div class="mt-2 col-12 col-md-8 col-md-6 m-auto">
              <validation-observer
                ref="rolesRules"
                tag="form"
              >
                <paper-simple>
                  <validation-provider
                    #default="{ errors }"
                    name="Name"
                    rules="required"
                    class="w-100 mb-1"
                  >
                    <input-field
                      name="organization_name"
                      label="Name"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <b-form-input
                        id="organization_name"
                        v-model="role.name"
                        placeholder="Add name here..."
                        :state="errors.length > 0 ? false : null"
                      />
                    </input-field>
                  </validation-provider>

                  <input-field
                    name="description"
                    label="Description"
                    label-position="top"
                  >
                    <template v-slot="{ id }">
                      <b-form-textarea
                        :id="id"
                        v-model="role.description"
                        placeholder="Add role description here..."
                      />
                    </template>
                  </input-field>
                </paper-simple>
              </validation-observer>
            </div>

            <div class="mt-2 col-md-8 col-md-6 m-auto">
              <div class="default-table">
                <div class="px-1 pt-1 d-block d-md-flex align-items-center">
                  <h6 class="text-main">
                    Assigned Permissions ({{ totalPermissions }})
                  </h6>
                  <b-button
                    variant="outline-primary"
                    class="ml-auto"
                    @click="openPermissions()"
                  >
                    <GjIcon
                      class="icon-left"
                      name="Plus"
                    />
                    Add Permissions
                  </b-button>
                </div>
                <b-table
                  ref="table"
                  class="loaded"
                  :items="permissions"
                  :fields="fields"
                  show-empty
                  responsive
                  :per-page="pageSize"
                  :current-page="currentPage"
                  select-mode="multi"
                  @row-clicked="onRowClicked"
                >
                  <template #empty>
                    <div class="no-permissions">
                      <p>
                        No permissions have been assigned yet...
                      </p>
                    </div>
                  </template>
                  <template v-slot:head(selected)="">
                    <a
                      @click="selectAllRows()"
                    ><select-button
                      :selected="allSelected"
                    /></a>
                  </template>
                  <template #cell(selected)="{ item }">
                    <select-button :selected="isSelected(item.id)" />
                  </template>
                </b-table>

                <div
                  v-if="totalCount > 0"
                  class="default-pagination"
                >
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalCount"
                    :per-page="pageSize"
                    first-number
                    last-number
                  />
                  <div class="results">
                    Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-slot:footer>
          <b-button
            variant="bg-white"
            @click="onCancel()"
          >
            Cancel
          </b-button>
          <button-loading
            variant="bg-primary"
            class="ml-1"
            :spinning="addingRole || updatingRole"
            @click="validationRole()"
          >Save Changes</button-loading>
        </template>
      </form-wrapper>
    </template>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import InputField from '@/components/form/InputField.vue'
import Permissions from './Permissions.vue'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    InputField,
    Permissions,
  },
  props: {
    details: {
      type: Boolean,
      default: false,
    },
    editRole: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      required,
      showPermissions: false,
      loading: true,
      role: {
        name: '',
        description: '',
      },
      fields: [
        { key: 'selected', label: '' },
        { key: 'title', label: 'Permission' },
      ],
      currentPage: 1,
      pageSize: 10,
      selectedItems: [],
    }
  },
  computed: {
    resultInfo() {
      return this.$store.state.roles.permissionsResultInfo
    },
    totalCount() {
      return this.permissions.length
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    permissions() {
      return this.$store.getters['roles/selectedPermissions']
    },
    totalPermissions() {
      return this.selectedItems.length
    },
    addingRole() {
      return this.$store.state.roles.addingRole
    },
    updatingRole() {
      return this.$store.state.roles.updatingRole
    },
    currentItems() {
      return this.permissions.slice(this.first - 1, this.last)
    },
    allSelected() {
      return (
        this.currentItems.length !== 0
        && this.currentItems.every(i => this.selectedItems.find(s => s.id === i.id))
      )
    },
  },
  watch: {
    permissions() {
      this.selectedItems = this.permissions
    },
  },
  created() {
    if (this.details) {
      this.role = this.editRole
      this.selectedItems = this.role.permissions
      this.setPermissions(this.selectedItems)
    }
  },
  beforeDestroy() {
    this.setPermissions([])
  },
  methods: {
    validationRole() {
      return new Promise((resolve, reject) => {
        this.$refs.rolesRules.validate().then(success => {
          if (success) {
            resolve(true)
            this.onSave()
          } else {
            reject()
          }
        })
      })
    },
    onSave() {
      const perm = []
      for (let i = 0; i < this.selectedItems.length; i++) {
        perm.push(this.selectedItems[i].id)
      }
      this.$emit('submit', {
        name: this.role.name,
        description: this.role.description,
        permissions: perm,
      })
    },
    onCancel() {
      this.$emit('close')
    },
    onRowSelected(items) {
      this.selectedItems = items
    },

    selectAllRows() {
      if (this.allSelected) {
        for (let i = 0; i < this.currentItems.length; i++) {
          this.selectedItems = this.selectedItems.filter(
            item => item.id !== this.currentItems[i].id,
          )
        }
      } else {
        for (let i = 0; i < this.currentItems.length; i++) {
          if (
            !this.selectedItems.some(
              item => item.id === this.currentItems[i].id,
            )
          ) {
            this.selectedItems.push(this.currentItems[i])
          }
        }
      }
    },
    onRowClicked(item) {
      if (this.selectedItems.some(i => i.id === item.id)) {
        this.selectedItems = this.selectedItems.filter(i => i.id !== item.id)
        return
      }

      this.selectedItems.push(item)
    },
    isSelected(id) {
      return this.selectedItems.some(item => id === item.id)
    },
    openPermissions() {
      this.showPermissions = true
      this.setPermissions(this.selectedItems)
    },
    setPermissions(items) {
      this.$store.commit('roles/SET_SELECTED_PERMISSIONS', items)
    },
  },
}
</script>

<style lang="scss" scoped>
.role {
  ul {
    padding-inline-start: 0;
    border-bottom: 1px solid #e0e5eb;
    li {
      margin-left: 25px;
    }
  }
  .mr-34 {
    margin-right: 34px;
  }

  .no-permissions {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100px;
  }

  .default-pagination {
    display: flex;
    padding-bottom: 10px;
    align-items: center;
    .pagination {
      border-bottom: none;
      margin-bottom: 0;
    }

    ul {
      li {
        margin-left: 10px;
      }
    }

    .results {
      margin-left: 10px;
    }
  }
}
</style>
